// Iter 80b: Job Board sandbox switch for the Settings → Platform panel.
// Reads/writes `/api/jobs/settings` (super admin only for PATCH).

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Briefcase, ShieldAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";

export function JobBoardSandboxToggle() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/jobs/settings").then((r) => setSettings(r.data)).catch(() => setSettings(null));
  }, []);

  if (!settings) return null;

  const toggle = async (v) => {
    try {
      setSaving(true);
      const { data } = await api.patch("/jobs/settings", { sandbox: v });
      setSettings(data);
      toast.success(v ? "Job Board is in sandbox — no emails leave the app" : "Job Board is LIVE — parking emails will send");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3"
         data-testid="jobs-sandbox-panel">
      <div className="flex items-start gap-2">
        <Briefcase className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div>
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
            Job Board sandbox
            {settings.sandbox
              ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">Sandbox</span>
              : <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-emerald-700">Live</span>}
          </div>
          <div className="text-xs text-slate-400">
            When ON, park + re-warm emails are audit-only. Flip to LIVE once the templates read right and candidates should receive them.
          </div>
          {!isSuper && <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-slate-500"><ShieldAlert className="h-3 w-3" /> Super admin only</div>}
        </div>
      </div>
      <Switch checked={!!settings.sandbox}
              disabled={!isSuper || saving}
              onCheckedChange={toggle}
              data-testid="jobs-sandbox-switch" />
      {saving && <Loader2 className="ml-2 h-4 w-4 animate-spin text-slate-400" />}
    </div>
  );
}
